import React, { useState, useEffect, useRef } from 'react';
import { ImageIcon, ImagePlus, Plus, Trash2, Loader2, ClipboardList, X } from 'lucide-react';
import ActivityLayout, {
    Toggle, SettingsGroup, SettingsRow,
    AIButton, AIUndoButton, PollResults,
} from './ActivityLayout';
import Tooltip from '../../components/ui/Tooltip';
import styles from './ActivityLayout.module.css';
import useActivityContent from '../../hooks/useActivityContent';
import ParticipantExperience from '../join/ParticipantExperience';
import { API_PATH, request } from '../../config/api';
import { useConfirm } from '../../store/ConfirmDialogContext';

const DEFAULT_SETTINGS = {
    allowMultiple: false,
    pollResults: 'hidden',
    isLocked: false,
    allowUpdateResponse: true,
    description: false,
    descText: '',
};

const DUMMY_DATA = {
    question: 'Which app UI design do you prefer?',
    options: [
        { id: 'opt1', imageUrl: 'https://placehold.co/400x300/6366f1/white?text=Design+A', label: 'Design A – Minimal' },
        { id: 'opt2', imageUrl: 'https://placehold.co/400x300/ec4899/white?text=Design+B', label: 'Design B – Bold' },
        { id: 'opt3', imageUrl: 'https://placehold.co/400x300/f59e0b/white?text=Design+C', label: 'Design C – Colorful' },
        { id: 'opt4', imageUrl: 'https://placehold.co/400x300/10b981/white?text=Design+D', label: 'Design D – Dark Mode' },
    ],
    settings: { ...DEFAULT_SETTINGS, allowMultiple: false, description: true, descText: 'Pick the design that feels most intuitive to you.' },
};

// Compact per-option image uploader
const OptionImageUpload = ({ imageUrl, onImageChange }) => {
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !file.type.startsWith('image/')) return;
        setUploading(true);
        const formData = new FormData();
        formData.append('image', file);
        try {
            const res = await request(`${API_PATH}/upload`, { method: 'POST', body: formData });
            if (!res.ok) throw new Error('Upload failed');
            const data = await res.json();
            if (data.url) onImageChange(data.url);
        } catch (err) {
            console.error(err);
            alert('Failed to upload image. Please try again.');
        }
        finally { setUploading(false); if (e.target) e.target.value = ''; }
    };

    return (
        <div style={{ position: 'relative', width: 72, height: 72, flexShrink: 0 }}>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} />
            <div
                onClick={() => !uploading && fileRef.current?.click()}
                onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && !uploading) {
                        e.preventDefault();
                        fileRef.current?.click();
                    }
                }}
                role="button"
                tabIndex={0}
                aria-label={imageUrl ? 'Click to change image' : 'Click to upload image'}
                title={imageUrl ? 'Click to change image' : 'Click to upload image'}
                style={{
                    width: '100%', height: '100%',
                    border: `2px dashed ${imageUrl ? 'transparent' : 'var(--border)'}`,
                    borderRadius: 'var(--radius-lg)', cursor: uploading ? 'wait' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    overflow: 'hidden', background: imageUrl ? 'transparent' : 'var(--bg-secondary)',
                    transition: 'border-color 0.15s',
                }}
            >
                {imageUrl ? (
                    <img src={imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'calc(var(--radius-lg) - 2px)' }} />
                ) : uploading ? (
                    <Loader2 size={20} style={{ color: 'var(--text-muted)', animation: 'spin 1s linear infinite' }} />
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                        <ImagePlus size={20} style={{ color: 'var(--text-muted)' }} />
                        <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Upload</span>
                    </div>
                )}
            </div>
            {imageUrl && !uploading && (
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onImageChange(''); }}
                    title="Remove image"
                    aria-label="Remove image"
                    style={{ position: 'absolute', top: -7, right: -7, width: 20, height: 20, borderRadius: '50%', background: 'var(--error)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', lineHeight: 1 }}
                ><X size={12} strokeWidth={3} /></button>
            )}
        </div>
    );
};

const ImageChoice = ({ event, activity, isLaunched, onLaunch, onStop, onNext, onPrev, hasNext, hasPrev, onSettingsUpdated }) => {
    const confirm = useConfirm();
    const [question, setQuestion] = useState('');
    const [editingQuestion, setEditingQuestion] = useState(false);
    const [editingDesc, setEditingDesc] = useState(false);
    const [options, setOptions] = useState([
        { id: 'opt1', imageUrl: '', label: '' },
        { id: 'opt2', imageUrl: '', label: '' },
    ]);
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [isImproving, setIsImproving] = useState(false);
    const [isImprovingDesc, setIsImprovingDesc] = useState(false);
    const [previousQuestion, setPreviousQuestion] = useState(null);
    const [previousDescText, setPreviousDescText] = useState(null);

    const { content, isLoading, isSaving, saveStatus, saveContent } = useActivityContent(event?.id, activity?.id);

    useEffect(() => {
        if (!content) return;
        setQuestion(content.question || '');
        if (Array.isArray(content.options) && content.options.length) setOptions(content.options);
        if (content.settings) setSettings(s => ({ ...s, ...content.settings }));
    }, [content]);

    const buildContent = () => ({ question, options, settings });

    const validate = () => {
        if (!question.trim()) return 'Please enter a question.';
        if (options.some(o => !o.imageUrl)) return 'All options must have an image.';
        return null;
    };

    const handleImproveWithAI = async () => {
        if (!question.trim()) return;
        setIsImproving(true);
        try {
            const res = await request(`${API_PATH}/ai/suggest`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: 'improve', text: question }) });
            if (res.ok) { const data = await res.json(); if (data.suggestion) { setPreviousQuestion(question); setQuestion(data.suggestion); } }
        } catch (err) { console.error(err); } finally { setIsImproving(false); }
    };

    const handleImproveDescWithAI = async () => {
        const text = settings.descText?.trim() || '';
        if (!text) return;
        setIsImprovingDesc(true);
        try {
            const res = await request(`${API_PATH}/ai/suggest`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: 'improve', text }) });
            if (res.ok) { const data = await res.json(); if (data.suggestion) { setPreviousDescText(settings.descText); updateSetting('descText', data.suggestion); } }
        } catch (err) { console.error(err); } finally { setIsImprovingDesc(false); }
    };

    const handleUndoQuestion = () => { if (previousQuestion !== null) { setQuestion(previousQuestion); setPreviousQuestion(null); } };
    const handleUndoDesc = () => { if (previousDescText !== null) { updateSetting('descText', previousDescText); setPreviousDescText(null); } };

    const loadDummy = () => {
        setQuestion(DUMMY_DATA.question);
        setOptions(DUMMY_DATA.options);
        setSettings(s => ({ ...s, ...DUMMY_DATA.settings }));
    };

    const updateSetting = (key, val) => setSettings(s => ({ ...s, [key]: val }));
    const addOption = () => setOptions(prev => [...prev, { id: `opt${Date.now()}`, imageUrl: '', label: '' }]);
    const removeOption = async (id) => {
        const responseCount = activity?.response_count || 0;
        const ok = await confirm({
            title: 'Delete Option',
            message: 'Delete this option?',
            details: responseCount > 0 ? (
                <>
                    <span style={{ fontWeight: 600 }}>⚠️ This poll already has {responseCount} response{responseCount !== 1 ? 's' : ''}.</span>
                    <br />
                    If any of them chose this option, it will no longer appear in the results breakdown.
                </>
            ) : undefined,
            confirmText: 'Delete',
            tone: 'danger',
        });
        if (!ok) return;
        setOptions(prev => prev.filter(o => o.id !== id));
    };
    const updateOption = (id, field, val) => setOptions(prev => prev.map(o => o.id === id ? { ...o, [field]: val } : o));

    const participantPreview = (
        <ParticipantExperience step="activity" name="Preview" event={event} activity={{ ...activity, content: buildContent() }} isPreview />
    );

    const left = (
        <>
            <div className={styles.questionCard}>
                <div className={styles.questionContent}>
                    <div className={styles.questionLabelRow}>
                        <span className={styles.questionLabel}>Question <span className={styles.requiredStar}>*</span></span>
                        <span className={styles.aiActions}>
                            <AIButton onClick={handleImproveWithAI} loading={isImproving} />
                            {previousQuestion !== null && <AIUndoButton onClick={handleUndoQuestion} label="Undo" />}
                        </span>
                    </div>
                    {editingQuestion ? (
                        <input className={styles.questionInput} value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Type your question here..." autoFocus onBlur={() => setEditingQuestion(false)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') { e.preventDefault(); setEditingQuestion(false); } }} />
                    ) : (
                        <Tooltip title="Click to edit question" placement="top">
                            <div className={`${styles.questionTextDisplay} ${!question ? styles.questionTextPlaceholder : ''}`} role="button" tabIndex={0} onClick={() => setEditingQuestion(true)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditingQuestion(true); } }}>
                                {question || 'Type your question here...'}
                            </div>
                        </Tooltip>
                    )}

                    {settings.description && (
                        <div className={styles.questionDescription}>
                            <div className={styles.questionLabelRow}>
                                <span className={styles.descLabel}>Description</span>
                                <span className={styles.aiActions}>
                                    <AIButton onClick={handleImproveDescWithAI} loading={isImprovingDesc} />
                                    {previousDescText !== null && <AIUndoButton onClick={handleUndoDesc} label="Undo" />}
                                </span>
                            </div>
                            {editingDesc ? (
                                <input className={styles.descInput} value={settings.descText || ''} onChange={(e) => updateSetting('descText', e.target.value)} placeholder="Type description here..." autoFocus onBlur={() => setEditingDesc(false)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') { e.preventDefault(); setEditingDesc(false); } }} />
                            ) : (
                                <Tooltip title="Click to edit description" placement="top">
                                    <div className={`${styles.descTextDisplay} ${!settings.descText ? styles.descTextPlaceholder : ''}`} role="button" tabIndex={0} onClick={() => setEditingDesc(true)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditingDesc(true); } }}>
                                        {settings.descText || 'Type description here...'}
                                    </div>
                                </Tooltip>
                            )}
                        </div>
                    )}

                    <div className={styles.optionsCard}>
                        <div className={styles.optionsHeader}>
                            <span className={styles.optionsLabel}>Image Options</span>
                            <span className={styles.markCorrectHint}>Click thumbnail to upload image</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                            {options.map((opt, i) => (
                                <div key={opt.id} className={styles.optionRow} style={{ alignItems: 'center', gap: 'var(--space-3)' }}>
                                    <OptionImageUpload imageUrl={opt.imageUrl} onImageChange={(url) => updateOption(opt.id, 'imageUrl', url)} />
                                    <input
                                        className={styles.optionInput}
                                        value={opt.label}
                                        onChange={(e) => updateOption(opt.id, 'label', e.target.value)}
                                        placeholder={`Option ${String.fromCharCode(65 + i)} label (optional)`}
                                        style={{ flex: 1 }}
                                    />
                                    {options.length > 2 && (
                                        <button className={styles.optionRemoveBtn} onClick={() => removeOption(opt.id)} aria-label="Remove option">
                                            <Trash2 size={13} />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                        <div className={styles.optionsFooter}>
                            <button className={styles.addOptionBtn} onClick={addOption}>
                                <Plus size={14} /> Add image option
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );

    const right = (
        <SettingsGroup title="Settings">
            <SettingsRow label="Poll description" description="Add description visible to participants">
                <Toggle checked={settings.description} onChange={(e) => updateSetting('description', e.target.checked)} />
            </SettingsRow>
            <SettingsRow label="Allow multiple selections" description="Participants can select more than one image">
                <Toggle checked={settings.allowMultiple} onChange={(e) => updateSetting('allowMultiple', e.target.checked)} />
            </SettingsRow>
            <SettingsRow label="Allow participants to update response" description="Let participants change their answer after submitting">
                <Toggle checked={settings.allowUpdateResponse} onChange={(e) => updateSetting('allowUpdateResponse', e.target.checked)} />
            </SettingsRow>
            <PollResults value={settings.pollResults} onChange={v => updateSetting('pollResults', v)} />
        </SettingsGroup>
    );

    return (
        <ActivityLayout
            icon={<ImageIcon size={20} />} color="var(--cat-indigo)"
            title="Image Choice" eventName={event?.name}
            left={left} right={right} participantPreview={participantPreview}
            onSave={() => saveContent(buildContent())}
            onLoadExample={loadDummy}
            isSaving={isSaving} saveStatus={saveStatus} isLoading={isLoading}
            validate={validate} event={event} activity={activity} isLaunched={isLaunched}
            onLaunch={onLaunch} onStop={onStop} onNext={onNext} onPrev={onPrev}
            hasNext={hasNext} hasPrev={hasPrev} onSettingsUpdated={onSettingsUpdated}
        />
    );
};

export default ImageChoice;
